import { useMutation, useQueryClient } from 'react-query';
import { useEffect } from 'react';
import { scanSite } from '../api';
import { DOMAIN_DATA } from '../constants/queryData';
import { FIVE_MINUTES_IN_MILLISECONDS, withinDuration } from '../utils/dateTime';
import { sliceIntoChunks } from '../utils/sliceIntoChunks';

const SITE_SCAN_CHUNK_SIZE = 5;

const findDomain = (previousData, domainName) => {
  // check for domain match
  const apexDomain = previousData.find((d) => d.domainName === domainName);
  if (apexDomain) {
    return apexDomain;
  }

  // check for subdomain match
  return previousData.flatMap((d) => d.subdomains || []).find((s) => s.domainName === domainName);
};

export const useScanSites = async ({ userId, domains }) => {
  const queryClient = useQueryClient();

  const { mutateAsync: runSiteScanAsync } = useMutation({
    mutationFn: (domainName) => {
      // check if site has already been scanned in the last 5 minutes
      const previousData = queryClient.getQueryData([DOMAIN_DATA, userId]);
      const domain = findDomain(previousData, domainName);

      // only scan if the last check was more than 5 minutes ago
      return withinDuration(domain?.domainStatus?.checkDate, FIVE_MINUTES_IN_MILLISECONDS)
        ? Promise.resolve()
        : scanSite([{ domainName }]);
    },
    onSuccess: (results, domainName) => {
      if (results === undefined || results?.domainStatuses?.length === 0) return;

      // update the domain status in the query cache
      queryClient.setQueryData([DOMAIN_DATA, userId], (cache) =>
        cache.map((domain) =>
          domain.domainName === domainName
            ? { ...domain, domainStatus: results.domainStatuses[0] }
            : domain.subdomains
            ? {
                ...domain,
                subdomains: domain.subdomains.map((subdomain) =>
                  subdomain.domainName === domainName
                    ? { ...subdomain, domainStatus: results.domainStatuses[0] }
                    : subdomain
                ),
              }
            : domain
        )
      );
    },
  });

  useEffect(() => {
    if (domains.length > 0) {
      const scanSites = async () => {
        const chunks = sliceIntoChunks(domains, SITE_SCAN_CHUNK_SIZE);
        await chunks.reduce(async (prev, chunk) => {
          await prev;
          // Return a Promise all for the current chunk
          const results = await Promise.all(
            chunk.map((domain) => runSiteScanAsync(domain.domainName))
          );
          return results;
        }, Promise.resolve());
      };
      scanSites();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, domains.length]);
};
